import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import ExpenseHeadingServices from "../../../Services/QRCodeServices/ExpenseHeadingServices/ExpenseHeadingServices";
import ExpenseCategoryServices from "../../../Services/QRCodeServices/ExpenseCategoryServices/ExpenseCategoryServices";

const ExpenseHeadingAddEditModal = ({
  isOpen,
  toggle,
  isEdit,
  selectedHeading,
  onSuccess,
}) => {
  const [categories, setCategories] = useState([]);
  // Validation schema using Yup
  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string(),
    categoryId: Yup.number()
      .min(1, "Please select a category")
      .required("Category is required"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      title: selectedHeading?.title || "",
      description: selectedHeading?.description || "",
      categoryId: selectedHeading?.categoryId || "",
    },
    validationSchema: validationSchema,
    enableReinitialize: true,

    onSubmit: async (values) => {
      try {
        const payload = {
          id: isEdit ? selectedHeading.id : undefined,
          title: values.title,
          description: values.description,
          categoryId: values.categoryId,
        };

        if (isEdit) {
          await ExpenseHeadingServices.update(selectedHeading.id, payload); // Hypothetical update method
          toast.success("Expense Heading updated successfully!");
        } else {
          await ExpenseHeadingServices.create(payload); // Hypothetical create method
          toast.success("Expense Heading created successfully!");
        }
        onSuccess();
        formik.resetForm();
      } catch (error) {
        toast.error(error.response?.data || "Failed to save expense category");
        console.error("Error saving expense category:", error);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    toggle();
  };

  const fetchCategory = async () => {
    try {
      const response = await ExpenseCategoryServices.getList();
      setCategories(response);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={handleCancel}>
        {isEdit ? "Edit Expense Heading" : "Create Expense Heading"}
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="title">Title</Label>
              <Input
                type="text"
                id="title"
                name="title"
                placeholder="Enter Title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                invalid={formik.touched.title && formik.errors.title}
              />
              {formik.touched.title && formik.errors.title && (
                <FormFeedback>{formik.errors.title}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="categoryId">Category</Label>
              <Input
                type="select"
                id="categoryId"
                name="categoryId"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.categoryId}
                invalid={formik.touched.categoryId && formik.errors.categoryId}
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </Input>
              {formik.touched.categoryId && formik.errors.categoryId && (
                <FormFeedback>{formik.errors.categoryId}</FormFeedback>
              )}
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="description">Description</Label>
              <Input
                type="textarea"
                id="description"
                name="description"
                placeholder="Enter Description"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                invalid={
                  formik.touched.description && formik.errors.description
                }
              />
              {formik.touched.description && formik.errors.description && (
                <FormFeedback>{formik.errors.description}</FormFeedback>
              )}
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" color="success" disabled={formik.isSubmitting}>
            {isEdit ? "Update" : "Save"}
          </Button>
          <Button color="danger" onClick={handleCancel} type="button">
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ExpenseHeadingAddEditModal;
